import React, { useState, useEffect, useCallback } from 'react';
import { Users, GitCompare, Eye, FolderPlus } from 'lucide-react';
import { colors, API, fmt, faseColor, authHeaders } from '../utils/theme';
import { abrirVerComo } from '../utils/verComo';
import {
  PageHeader, SearchBar, Table, Th, Td, TrHover, Badge, ActionButtons,
  Modal, FormField, Input, Select, TextArea, Button, LoadingSpinner,
  ConfirmDialog, EmptyState,
} from '../components/UI';
import useSort from '../hooks/useSort';
import TotaisDeLimite, { somaDe } from '../components/TotaisDeLimite';

const FASES = ['NORMAL', 'ATENÇÃO', 'ALTO', 'URGENTE', 'CRÍTICO'];
const CLASSIFICACOES = ['XMiles Ativo', 'XMiles Inativo', 'Pessoal', 'Terceiro', 'Outro'];
const TIPOS = [
  { value: 'INVESTIDOR', label: 'Investidor' },
  { value: 'CLIENTE', label: 'Cliente (só controle de gastos)' },
];

const emptyForm = { nome: '', cpf: '', cep: '', classificacao: 'XMiles Ativo', fase: 'NORMAL', status: 'ATIVO', tipo: 'INVESTIDOR', scp: false, plano_acao: '', observacoes: '' };

export default function TitularesPage() {
  const [titulares, setTitulares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selecionados, setSelecionados] = useState(new Set());
  const [modoSelecao, setModoSelecao] = useState(false);
  const [bulkCampo, setBulkCampo] = useState('status'); // 'status' | 'tipo' | 'scp'
  const [bulkStatusSel, setBulkStatusSel] = useState('INATIVO');
  const [bulkTipoSel, setBulkTipoSel] = useState('CLIENTE');
  const [bulkScpSel, setBulkScpSel] = useState('true');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [conferirOpen, setConferirOpen] = useState(false);
  const [conferirData, setConferirData] = useState(null);
  const [conferirLoading, setConferirLoading] = useState(false);
  const [conferirAcao, setConferirAcao] = useState(null);

  const load = useCallback(() => {
    // com_limites: limite, utilizado e disponível consolidados dos cartões
    // ativos de cada titular (com o número do banco nos conectados ao Open Finance).
    fetch(`${API}/titulares?com_limites=true`, { headers: authHeaders() }).then(r => r.json()).then(setTitulares).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (detailId) {
      fetch(`${API}/titulares/${detailId}`, { headers: authHeaders() }).then(r => r.json()).then(setDetail).catch(console.error);
    } else {
      setDetail(null);
    }
  }, [detailId]);

  const filtered = titulares.filter(t => {
    if (search && !t.nome.toLowerCase().includes(search.toLowerCase())) return false;
    if (filtroTipo && (t.tipo || 'INVESTIDOR') !== filtroTipo) return false;
    if (filtroStatus && (t.status || 'ATIVO') !== filtroStatus) return false;
    return true;
  });
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered, 'utilizado_efetivo', 'desc');
  const sortState = { sortKey, sortDir };

  const toggleSelecao = (id) => setSelecionados(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleTodos = () => {
    if (selecionados.size === sorted.length) setSelecionados(new Set());
    else setSelecionados(new Set(sorted.map(t => t.id)));
  };

  const aplicarBulk = async () => {
    if (!selecionados.size) return;
    setBulkLoading(true);
    try {
      const urlPor = {
        status: `${API}/titulares/bulk-status`,
        tipo:   `${API}/titulares/bulk-tipo`,
        scp:    `${API}/titulares/bulk-scp`,
      };
      const bodyPor = {
        status: { titular_ids: [...selecionados], status: bulkStatusSel },
        tipo:   { titular_ids: [...selecionados], tipo:   bulkTipoSel },
        scp:    { titular_ids: [...selecionados], scp:    bulkScpSel === 'true' },
      };
      const url = urlPor[bulkCampo];
      const body = bodyPor[bulkCampo];
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      const valorTxt = {
        status: bulkStatusSel,
        tipo:   bulkTipoSel,
        scp:    bulkScpSel === 'true' ? 'SCP' : 'sem SCP',
      }[bulkCampo];
      alert(`✓ ${data.atualizados} titulares atualizados (${bulkCampo} → ${valorTxt})`);
      setSelecionados(new Set());
      setModoSelecao(false);
      load();
    } catch (e) { alert('Erro: ' + e.message); }
    setBulkLoading(false);
  };

  const openCreate = () => { setEditId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ nome: t.nome, cpf: t.cpf || '', cep: t.cep || '', classificacao: t.classificacao || '', fase: t.fase || 'NORMAL', status: t.status || 'ATIVO', tipo: t.tipo || 'INVESTIDOR', scp: !!t.scp, plano_acao: t.plano_acao || '', observacoes: t.observacoes || '' });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.nome.trim()) return alert('Nome é obrigatório');
    setSaving(true);
    try {
      const method = editId ? 'PUT' : 'POST';
      const url = editId ? `${API}/titulares/${editId}` : `${API}/titulares`;
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      setModalOpen(false);
      load();
    } catch (e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  // === Impersonar (Ver como) — só ADMIN. Salva token atual em backup,
  // troca por token Titular daquele titular e recarrega. Banner global no
  // Layout permite voltar.
  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('xm_usuario') || '{}');
      return (u.perfis || []).includes('ADMIN');
    } catch { return false; }
  })();

  // Operações: quem está na mesma se enxerga. Só o ADMIN muda, porque mexer
  // aqui altera o que os gestores veem no sistema inteiro.
  const [operacoes, setOperacoes] = useState([]);
  const carregarOperacoes = useCallback(() => {
    if (!isAdmin) return;
    fetch(`${API}/titulares/operacoes`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setOperacoes(Array.isArray(d) ? d : []))
      .catch(() => setOperacoes([]));
  }, [isAdmin]);
  useEffect(() => { carregarOperacoes(); }, [carregarOperacoes]);

  // Criar operação (05/10/2026): não havia caminho nenhum pra isso — a coluna
  // Operação só movia titular entre as que já existiam, e o endpoint que cria
  // junto com a composição remove da operação quem não vier na lista. Aqui a
  // operação nasce VAZIA e o gestor vai movendo os titulares pela coluna, um a
  // um, vendo o efeito a cada passo.
  const [operacoesOpen, setOperacoesOpen] = useState(false);

  // Só apaga operação VAZIA — o servidor recusa as cheias, porque apagar uma
  // operação com gente dentro tiraria cada um do escopo dos colegas em silêncio.
  const excluirOperacao = async (op) => {
    if (!window.confirm(`Excluir a operação de visibilidade "${op.nome}"?`)) return;
    try {
      const r = await fetch(`${API}/titulares/operacoes/${op.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Falha');
      carregarOperacoes();
    } catch (e) { alert(`Erro ao excluir: ${e.message}`); }
  };

  const criarOperacao = async () => {
    const nome = window.prompt('Nome da nova operação (ex.: GRUPO):');
    if (nome === null) return;
    if (!nome.trim()) { alert('O nome é obrigatório.'); return; }
    try {
      const r = await fetch(`${API}/titulares/operacoes/nova`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ nome: nome.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Falha');
      carregarOperacoes();
      alert(d.criada
        ? `Operação "${d.operacao.nome}" criada, ainda sem ninguém dentro.\n\nAgora use a coluna Operação pra mover cada titular pra ela.`
        : `A operação "${d.operacao.nome}" já existia — nada foi alterado.`);
    } catch (e) { alert(`Erro ao criar operação: ${e.message}`); }
  };

  const moverOperacao = async (t, valor) => {
    const destino = valor === '' ? null : parseInt(valor, 10);
    const nomeDestino = destino ? (operacoes.find(o => Number(o.id) === destino)?.nome || 'a operação') : 'nenhuma operação';
    if (!window.confirm(`Mover "${t.nome}" para ${nomeDestino}?\n\nIsso muda quem enxerga este titular: só quem está na mesma operação (e o ADMIN) passa a ver.`)) return;
    try {
      const r = await fetch(`${API}/titulares/${t.id}/operacao`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ operacao_id: destino }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Falha');
      load();
    } catch (e) { alert(`Erro ao mover de operação: ${e.message}`); }
  };

  const verComo = async (t) => {
    if (!isAdmin) return;
    // Abre numa aba à parte (03/11/2026) — esta continua como ADMIN.
    try {
      await abrirVerComo(async () => {
        const r = await fetch(`${API}/auth/impersonar`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ titular_id: t.id }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        return data;
      });
    } catch (e) {
      alert('Erro ao impersonar: ' + e.message);
    }
  };

  const handleDelete = async () => {
    try {
      const r = await fetch(`${API}/titulares/${deleteId}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setDeleteId(null);
      load();
    } catch (e) {
      alert('Erro ao excluir: ' + e.message);
    }
  };

  const carregarConferir = useCallback(async () => {
    setConferirLoading(true);
    setConferirData(null);
    try {
      const res = await fetch(`${API}/xmiles/comparar-titulares`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      setConferirData(data);
    } catch (e) {
      alert('Erro ao comparar com Manager: ' + e.message);
      setConferirOpen(false);
    }
    setConferirLoading(false);
  }, []);

  const importarDoManager = async (xmiles_ids) => {
    if (!window.confirm(`Importar ${xmiles_ids.length} titular(es) do Manager?`)) return;
    setConferirAcao('importar');
    try {
      const res = await fetch(`${API}/xmiles/importar-titulares-manager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ xmiles_ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      alert(`✓ ${data.importados} importados, ${data.ja_existia} já existiam.`);
      await carregarConferir();
      await load();
    } catch (e) { alert('Erro: ' + e.message); }
    setConferirAcao(null);
  };

  const marcarLocaisComoCliente = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Marcar ${ids.length} titular(es) como CLIENTE?`)) return;
    setConferirAcao('cliente');
    try {
      const res = await fetch(`${API}/titulares/bulk-tipo`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ titular_ids: ids, tipo: 'CLIENTE' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      alert(`✓ ${data.atualizados} marcados como CLIENTE.`);
      await carregarConferir();
      await load();
    } catch (e) { alert('Erro: ' + e.message); }
    setConferirAcao(null);
  };

  const excluirLocais = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`EXCLUIR ${ids.length} titular(es) sem cartões?\nAção irreversível.`)) return;
    setConferirAcao('excluir');
    let ok = 0, fail = 0;
    const motivos = [];
    for (const id of ids) {
      try {
        const r = await fetch(`${API}/titulares/${id}`, { method: 'DELETE', headers: authHeaders() });
        const data = await r.json().catch(() => ({}));
        if (r.ok) ok++; else { fail++; motivos.push(`#${id}: ${data.error || `HTTP ${r.status}`}`); }
      } catch (e) { fail++; motivos.push(`#${id}: ${e.message}`); }
    }
    let msg = `✓ ${ok} excluídos`;
    if (fail) msg += `, ${fail} falharam:\n` + motivos.slice(0, 5).join('\n') + (motivos.length > 5 ? `\n…+${motivos.length - 5}` : '');
    alert(msg);
    await carregarConferir();
    await load();
    setConferirAcao(null);
  };

  const mergeAmbiguo = async (source_id, target_id) => {
    if (source_id === target_id) return;
    if (!window.confirm(`Mesclar titular #${source_id} em #${target_id}?\nTodos os cartões/transações migram pro target.`)) return;
    setConferirAcao(`merge-${source_id}`);
    try {
      const res = await fetch(`${API}/titulares/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ source_id, target_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      alert(`✓ Merge concluído.`);
      await carregarConferir();
      await load();
    } catch (e) { alert('Erro: ' + e.message); }
    setConferirAcao(null);
  };

  useEffect(() => {
    if (conferirOpen && !conferirData && !conferirLoading) carregarConferir();
  }, [conferirOpen, conferirData, conferirLoading, carregarConferir]);

  if (loading) return <LoadingSpinner />;

  if (detail) {
    return (
      <div>
        <Button variant="outline" color={colors.textMuted} onClick={() => setDetailId(null)} style={{ marginBottom: 16 }}>
          ← Voltar para lista
        </Button>

        <div style={{
          background: colors.card, borderRadius: 12, padding: 24, marginBottom: 20,
          border: `1px solid ${colors.border}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{detail.nome}</h3>
              <div style={{ color: colors.textMuted, marginTop: 4 }}>{detail.classificacao}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: colors.danger }}>{fmt(detail.total_fatura)}</div>
              {isAdmin && <Badge text={detail.fase || 'N/A'} color={faseColor(detail.fase)} />}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 20 }}>
            <div><span style={{ fontSize: 12, color: colors.textMuted }}>Total Cartões</span><br /><strong>{detail.total_cartoes}</strong></div>
            <div><span style={{ fontSize: 12, color: colors.textMuted }}>Ativos</span><br /><strong style={{ color: colors.success }}>{detail.cartoes_ativos}</strong></div>
            {isAdmin && <div><span style={{ fontSize: 12, color: colors.textMuted }}>Atrasados</span><br /><strong style={{ color: detail.cartoes_atrasados > 0 ? colors.danger : colors.success }}>{detail.cartoes_atrasados}</strong></div>}
            <div><span style={{ fontSize: 12, color: colors.textMuted }}>Limite Total</span><br /><strong>{fmt(detail.total_limite)}</strong></div>
          </div>
        </div>

        {/* Cartões do titular */}
        <Table>
          <thead><tr>
            <Th>Cartão</Th><Th>Banco</Th><Th>Limite</Th><Th>Fatura</Th>
            {isAdmin && <><Th>Atraso</Th><Th>Fase</Th><Th>Serasa</Th></>}
          </tr></thead>
          <tbody>
            {(detail.cartoes || []).map(c => (
              <tr key={c.id}>
                <Td style={{ fontWeight: 600 }}>{c.nome_cartao}</Td>
                <Td>{c.banco_nome || '-'}</Td>
                <Td>{fmt(c.limite_total)}</Td>
                <Td style={{ color: colors.danger, fontWeight: 600 }}>{fmt(c.fatura_atual)}</Td>
                {isAdmin && <>
                  <Td style={{ color: c.dias_atraso > 0 ? colors.danger : colors.textDim }}>{c.dias_atraso > 0 ? `${c.dias_atraso}d` : '-'}</Td>
                  <Td><Badge text={c.fase || 'N/A'} color={faseColor(c.fase)} /></Td>
                  <Td>{c.serasa === 'SIM' ? <span style={{ color: colors.danger, fontWeight: 700 }}>SIM</span> : 'Não'}</Td>
                </>}
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Custos mensais */}
        {(detail.custos_mensais || []).length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Custos Mensais (Juros + IOF)</h3>
            <Table>
              <thead><tr>
                <Th>Mês</Th><Th>Total Fatura</Th><Th>Juros</Th><Th>IOF</Th><Th>Multas</Th><Th>Total Custos</Th>
              </tr></thead>
              <tbody>
                {detail.custos_mensais.map((c, i) => (
                  <tr key={i}>
                    <Td>{c.mes_referencia}</Td>
                    <Td>{fmt(c.total_fatura)}</Td>
                    <Td style={{ color: colors.danger }}>{fmt(c.total_juros)}</Td>
                    <Td>{fmt(c.total_iof)}</Td>
                    <Td>{fmt(c.total_multas)}</Td>
                    <Td style={{ fontWeight: 700, color: colors.danger }}>{fmt(c.total_custos)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Titulares" subtitle={`${filtered.length} de ${titulares.length} titulares`}
        onAdd={openCreate} addLabel="Criar novo Titular" />

      <SearchBar value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar titular...">
        <Select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Todos os tipos</option>
          <option value="INVESTIDOR">Investidor</option>
          <option value="CLIENTE">Cliente</option>
        </Select>
        <Select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
          style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Todos os status</option>
          <option value="ATIVO">Ativos</option>
          <option value="INATIVO">Inativos</option>
        </Select>
        <Button variant="outline" color={colors.info}
          onClick={() => { setConferirOpen(true); setConferirData(null); }}
          icon={GitCompare}
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          Conferir com Manager
        </Button>
        {isAdmin && (
          <Button variant="outline" color={colors.primary}
            onClick={() => setOperacoesOpen(true)} icon={FolderPlus}
            title="Grupos de visibilidade: quem enxerga quem. Não é o centro de fechamento (Operação X/J, Compras J) da Conciliação."
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            Visibilidade
          </Button>
        )}
        <Button variant="outline" color={modoSelecao ? colors.warning : colors.textMuted}
          onClick={() => { setModoSelecao(s => !s); setSelecionados(new Set()); }}
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          {modoSelecao ? '✕ Cancelar' : '☑ Selecionar'}
        </Button>
      </SearchBar>

      {filtered.length > 0 && (
        <TotaisDeLimite limite={somaDe(filtered, 'limite_efetivo')} utilizado={somaDe(filtered, 'utilizado_efetivo')}
          disponivel={somaDe(filtered, 'disponivel_efetivo')}
          subtitulo={`${filtered.length} titular(es) · ${somaDe(filtered, 'cartoes_ativos')} cartões ativos`
            + (somaDe(filtered, 'cartoes_open_finance') ? ` · ${somaDe(filtered, 'cartoes_open_finance')} com limite do banco` : '')} />
      )}

      {filtered.length === 0 ? (
        <EmptyState message="Nenhum titular encontrado" icon={Users} />
      ) : (
        <Table>
          <thead><tr>
            {modoSelecao && (
              <Th align="center" style={{ width: 40 }}>
                <input type="checkbox" checked={selecionados.size === sorted.length && sorted.length > 0}
                  onChange={toggleTodos} style={{ cursor: 'pointer' }} />
              </Th>
            )}
            <Th sortKey="nome" sortState={sortState} onSort={toggleSort}>Nome</Th>
            <Th sortKey="tipo" sortState={sortState} onSort={toggleSort}>Tipo</Th>
            <Th sortKey="scp" sortState={sortState} onSort={toggleSort} align="center">SCP</Th>
            <Th sortKey="classificacao" sortState={sortState} onSort={toggleSort}>Classificação</Th>
            <Th sortKey="total_cartoes" sortState={sortState} onSort={toggleSort} align="center">Cartões</Th>
            <Th sortKey="cartoes_ativos" sortState={sortState} onSort={toggleSort} align="center">Ativos</Th>
            <Th sortKey="limite_efetivo" sortState={sortState} onSort={toggleSort}>Limite</Th>
            <Th sortKey="utilizado_efetivo" sortState={sortState} onSort={toggleSort}>Utilizado</Th>
            <Th sortKey="disponivel_efetivo" sortState={sortState} onSort={toggleSort}>Disponível</Th>
            <Th sortKey="pct_efetivo" sortState={sortState} onSort={toggleSort}>% Uso</Th>
            <Th sortKey="total_fatura" sortState={sortState} onSort={toggleSort}>Total Fatura</Th>
            {/* Atraso, Serasa e Fase: só o ADMIN (03/11/2026). */}
            {isAdmin && <Th sortKey="cartoes_atrasados" sortState={sortState} onSort={toggleSort} align="center">Atraso</Th>}
            {isAdmin && <Th sortKey="cartoes_serasa" sortState={sortState} onSort={toggleSort} align="center">Serasa</Th>}
            {isAdmin && <Th sortKey="fase" sortState={sortState} onSort={toggleSort}>Fase</Th>}
            {/* A operação decide quem enxerga o titular: gestor vinculado vê
                só os da própria operação. Ficava invisível, e titular fora
                dela simplesmente não aparecia pro gestor, sem explicação. */}
            <Th>Operação</Th>
            <Th align="center">Status</Th>
            <Th align="center">Ações</Th>
          </tr></thead>
          <tbody>
            {sorted.map(t => (
              <TrHover key={t.id} style={selecionados.has(t.id) ? { background: `${colors.primary}18` } : {}}>
                {modoSelecao && (
                  <Td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={selecionados.has(t.id)}
                      onChange={() => toggleSelecao(t.id)} style={{ cursor: 'pointer' }} />
                  </Td>
                )}
                <Td style={{ fontWeight: 600 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {t.nome}
                    {t.scp && (
                      <span title="SCP - Sociedade em Conta de Participação (operação independente)"
                        style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                          background: `${colors.warning}25`, color: colors.warning,
                          letterSpacing: '0.03em', border: `1px solid ${colors.warning}55`,
                        }}>SCP</span>
                    )}
                    {/* Chamado #62: o TechMiles mandou o cartão sem dizer de quem
                        é, e o sistema criou este titular com o NOME DO CARTÃO.
                        Pode não ser pessoa nenhuma — precisa alguém conferir se
                        é titular novo ou cartão de um titular que já existe.
                        Investidor (04/11/2026): o mesmo selo marca o cadastro
                        que ele enviou e a gestão ainda não aprovou. */}
                    {t.pendente_revisao && (
                      <span title={t.cadastro_status
                        ? 'Cadastro enviado pelo investidor, esperando aprovação da gestão. Aprove em Remuneração de Investidor → Investidores → Revisar e aprovar; é lá que se escolhe a operação dele.'
                        : 'Criado automaticamente pelo TechMiles sem informar o titular — o nome veio do cartão. Confira se é um titular novo ou se o cartão é de alguém que já existe (e nesse caso mescle).'}
                        style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                          background: `${colors.danger}25`, color: colors.danger,
                          letterSpacing: '0.03em', border: `1px solid ${colors.danger}55`,
                        }}>REVISAR</span>
                    )}
                  </div>
                </Td>
                <Td>
                  <Badge
                    text={t.tipo === 'CLIENTE' ? 'Cliente' : 'Investidor'}
                    color={t.tipo === 'CLIENTE' ? colors.textMuted : colors.primary}
                  />
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  {t.scp ? <Badge text="SIM" color={colors.warning} /> : <span style={{ color: colors.textDim, fontSize: 12 }}>—</span>}
                </Td>
                <Td style={{ color: colors.textMuted }}>{t.classificacao || '-'}</Td>
                <Td style={{ textAlign: 'center' }}>{t.total_cartoes}</Td>
                <Td style={{ textAlign: 'center', color: colors.success }}>{t.cartoes_ativos}</Td>
                <Td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {fmt(t.limite_efetivo)}
                  {t.cartoes_open_finance > 0 && (
                    <div style={{ fontSize: 10, color: colors.textMuted, fontWeight: 400 }}
                      title="Cartões com o limite informado pelo banco (Open Finance) e a soma dos limites do cadastro">
                      {t.cartoes_open_finance} no Open Finance
                      {Math.abs((t.limite_cadastro || 0) - (t.limite_efetivo || 0)) >= 0.01 && ` · cadastro ${fmt(t.limite_cadastro)}`}
                    </div>
                  )}
                </Td>
                <Td style={{ fontWeight: 600, color: colors.danger, whiteSpace: 'nowrap' }}>{fmt(t.utilizado_efetivo)}</Td>
                <Td style={{ fontWeight: 600, color: colors.success, whiteSpace: 'nowrap' }}>{fmt(t.disponivel_efetivo)}</Td>
                <Td>
                  {(() => {
                    const pct = Math.round(Number(t.pct_efetivo) || 0);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ background: colors.bg, borderRadius: 4, overflow: 'hidden', height: 6, width: 50 }}>
                          <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct > 90 ? colors.danger : pct > 70 ? colors.warning : colors.success, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 11, color: colors.textMuted }}>{pct}%</span>
                      </div>
                    );
                  })()}
                </Td>
                <Td style={{ fontWeight: 600, color: colors.danger }}>{fmt(t.total_fatura)}</Td>
                {isAdmin && <Td style={{ textAlign: 'center', color: t.cartoes_atrasados > 0 ? colors.danger : colors.success }}>{t.cartoes_atrasados}</Td>}
                {isAdmin && (
                  <Td style={{ textAlign: 'center' }}>
                    {t.cartoes_serasa > 0 ? <Badge text={`${t.cartoes_serasa}`} color={colors.danger} /> : '-'}
                  </Td>
                )}
                {isAdmin && <Td><Badge text={t.fase || 'N/A'} color={faseColor(t.fase)} /></Td>}
                <Td>
                  {isAdmin ? (
                    <select value={t.operacao_id || ''} onChange={e => moverOperacao(t, e.target.value)}
                      title="Quem está na mesma operação se enxerga. Fora dela, só o ADMIN e o próprio titular veem — menos o investidor convidado sem operação, que a operação dos investidores também vê."
                      style={{ background: colors.bg, color: t.operacao_id ? colors.text : colors.textDim,
                        border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 6px', fontSize: 12, maxWidth: 170 }}>
                      <option value="">— sem operação —</option>
                      {operacoes.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  ) : (
                    <span style={{ fontSize: 12, color: t.operacao_nome ? colors.text : colors.textDim }}>
                      {t.operacao_nome || '—'}
                    </span>
                  )}
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  <Badge
                    text={t.status === 'INATIVO' ? 'Inativo' : 'Ativo'}
                    color={t.status === 'INATIVO' ? colors.textMuted : colors.success}
                  />
                </Td>
                <Td>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {isAdmin && (
                      <button
                        onClick={() => verComo(t)}
                        title={`Ver o sistema como ${t.nome} (impersonação temporária)`}
                        style={{
                          background: `${colors.warning}22`, color: colors.warning,
                          border: `1px solid ${colors.warning}55`, borderRadius: 6,
                          padding: '4px 8px', cursor: 'pointer', display: 'flex',
                          alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
                        }}>
                        <Eye size={12} /> Ver como
                      </button>
                    )}
                    <ActionButtons
                      onView={() => setDetailId(t.id)}
                      onEdit={() => openEdit(t)}
                      onDelete={() => setDeleteId(t.id)}
                    />
                  </div>
                </Td>
              </TrHover>
            ))}
          </tbody>
        </Table>

      )}

      {/* Barra de ação bulk */}
      {selecionados.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: colors.card, border: `1px solid ${colors.border}`,
          borderRadius: 12, padding: '12px 20px', display: 'flex', alignItems: 'center',
          gap: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 1000, flexWrap: 'wrap',
        }}>
          <span style={{ color: colors.text, fontWeight: 600 }}>
            ✓ {selecionados.size} selecionado{selecionados.size !== 1 ? 's' : ''}
          </span>
          <div style={{ width: 1, height: 24, background: colors.border }} />
          <span style={{ color: colors.textMuted, fontSize: 13 }}>Alterar:</span>
          <div style={{ display: 'flex', gap: 2, background: colors.bg, borderRadius: 6, padding: 2, border: `1px solid ${colors.border}` }}>
            {[
              { v: 'status', label: 'Status' },
              { v: 'tipo',   label: 'Tipo' },
              { v: 'scp',    label: 'SCP' },
            ].map(o => (
              <button key={o.v} onClick={() => setBulkCampo(o.v)} style={{
                padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: bulkCampo === o.v ? colors.primary : 'transparent',
                color: bulkCampo === o.v ? '#fff' : colors.textMuted, transition: 'all 0.15s',
              }}>{o.label}</button>
            ))}
          </div>
          <span style={{ color: colors.textMuted, fontSize: 13 }}>para:</span>
          {bulkCampo === 'status' && (
            <select value={bulkStatusSel} onChange={e => setBulkStatusSel(e.target.value)}
              style={{ background: colors.input, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 13 }}>
              <option value="ATIVO">Ativo</option>
              <option value="INATIVO">Inativo</option>
            </select>
          )}
          {bulkCampo === 'tipo' && (
            <select value={bulkTipoSel} onChange={e => setBulkTipoSel(e.target.value)}
              style={{ background: colors.input, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 13 }}>
              <option value="INVESTIDOR">Investidor</option>
              <option value="CLIENTE">Cliente</option>
            </select>
          )}
          {bulkCampo === 'scp' && (
            <select value={bulkScpSel} onChange={e => setBulkScpSel(e.target.value)}
              style={{ background: colors.input, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 13 }}>
              <option value="true">SCP</option>
              <option value="false">Sem SCP</option>
            </select>
          )}
          <Button onClick={aplicarBulk} disabled={bulkLoading} style={{ padding: '6px 16px', fontSize: 13 }}>
            {bulkLoading ? 'Aplicando...' : 'Aplicar'}
          </Button>
          <Button variant="outline" color={colors.textMuted} onClick={() => { setSelecionados(new Set()); setModoSelecao(false); }}
            style={{ padding: '6px 12px', fontSize: 13 }}>
            Cancelar
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {/* Operações de VISIBILIDADE (06/10/2026). "Operação" tem dois sentidos
          no sistema e foram confundidos: isto aqui é quem enxerga quem; a
          coluna OPERAÇÃO da Conciliação é o centro de fechamento. */}
      <Modal isOpen={operacoesOpen} onClose={() => setOperacoesOpen(false)}
        title="Operações de visibilidade" width={560}>
        <div style={{ padding: 20 }}>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>
            Titulares na mesma operação se enxergam (gestor vê o conjunto; investidor continua vendo só o seu).
            Isto <strong>não</strong> é o centro de fechamento — Operação X, Operação J, Compras J — da coluna
            OPERAÇÃO na Conciliação.
          </p>
          {operacoes.length === 0 ? (
            <div style={{ fontSize: 13, color: colors.textDim, padding: '12px 0' }}>Nenhuma operação cadastrada.</div>
          ) : operacoes.map(op => {
            const membros = op.titulares || [];
            return (
              <div key={op.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: colors.text }}>{op.nome}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {membros.length ? membros.map(t => t.nome).join(', ') : 'vazia'}
                  </div>
                </div>
                {membros.length === 0 ? (
                  <Button size="sm" variant="outline" color={colors.danger} onClick={() => excluirOperacao(op)}>
                    Excluir
                  </Button>
                ) : (
                  <span title="Mova os titulares pela coluna Operação antes de excluir"
                    style={{ fontSize: 12, color: colors.textDim, whiteSpace: 'nowrap' }}>
                    {membros.length} titular(es)
                  </span>
                )}
              </div>
            );
          })}
          <div style={{ marginTop: 16 }}>
            <Button color={colors.primary} icon={FolderPlus} onClick={criarOperacao}>Nova operação de visibilidade</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editId ? 'Editar Titular' : 'Criar novo Titular'}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
          <FormField label="Nome" required>
            <Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome do titular" />
          </FormField>
          <FormField label="CPF">
            <Input value={form.cpf} onChange={e => setForm(f => ({ ...f, cpf: e.target.value.replace(/\D/g, '').slice(0, 11) }))} placeholder="00000000000" maxLength={11} />
          </FormField>
          <FormField label="CEP" title="Para senha PDF: Porto Seguro = 4 dígitos CPF + 5 dígitos CEP">
            <Input value={form.cep} onChange={e => setForm(f => ({ ...f, cep: e.target.value.replace(/\D/g, '').slice(0, 8) }))} placeholder="00000000" maxLength={8} />
          </FormField>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Tipo" title="Investidor aparece no Painel do Investidor; Cliente só serve pra controle de gastos da operação (não tem painel próprio).">
            <Select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </FormField>
          <FormField label="Classificação">
            <Select value={form.classificacao} onChange={e => setForm(f => ({ ...f, classificacao: e.target.value }))}>
              {CLASSIFICACOES.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </FormField>
          {isAdmin && (
            <FormField label="Fase">
              <Select value={form.fase} onChange={e => setForm(f => ({ ...f, fase: e.target.value }))}>
                {FASES.map(f => <option key={f} value={f}>{f}</option>)}
              </Select>
            </FormField>
          )}
          <FormField label="Status">
            <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="ATIVO">Ativo</option>
              <option value="INATIVO">Inativo</option>
            </Select>
          </FormField>
          <FormField label="SCP" title="Sociedade em Conta de Participação — operação independente. Quando marcado, o titular pode vir de outro Manager e fica escondido dos dashboards por padrão (toggle 'Incluir SCP' pra ver).">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.scp}
                onChange={e => setForm(f => ({ ...f, scp: e.target.checked }))}
                style={{ accentColor: colors.warning }} />
              <span style={{ fontSize: 13, color: form.scp ? colors.warning : colors.textMuted, fontWeight: form.scp ? 600 : 400 }}>
                {form.scp ? 'Titular SCP (independente)' : 'Titular regular'}
              </span>
            </label>
          </FormField>
        </div>
        {/* Fase e Plano de Ação: só o ADMIN (03/11/2026). */}
        {isAdmin && (
          <FormField label="Plano de Ação">
            <TextArea value={form.plano_acao} onChange={e => setForm(f => ({ ...f, plano_acao: e.target.value }))} placeholder="Descreva o plano..." />
          </FormField>
        )}
        <FormField label="Observações">
          <TextArea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} placeholder="Observações gerais..." />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button variant="outline" color={colors.textMuted} onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : editId ? 'Salvar Alterações' : 'Criar Titular'}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Titular" message="Tem certeza que deseja excluir este titular? Esta ação não pode ser desfeita."
      />

      {/* Modal: Conferir com Manager */}
      {conferirOpen && (
        <Modal isOpen={conferirOpen} onClose={() => setConferirOpen(false)} title="Conferir Titulares com o Manager">
          {conferirLoading || !conferirData ? (
            <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>
              Carregando lista do Manager…
            </div>
          ) : (
            <ConferirManagerContent
              data={conferirData}
              acao={conferirAcao}
              titulares={titulares}
              onImportarTodos={() => importarDoManager(conferirData.so_manager.map(m => m.xmiles_id))}
              onImportarUm={(xid) => importarDoManager([xid])}
              onMarcarClienteTodos={() => marcarLocaisComoCliente(conferirData.so_local.map(l => l.id))}
              onMarcarClienteUm={(id) => marcarLocaisComoCliente([id])}
              onExcluirSemCartoes={() => excluirLocais(conferirData.so_local.filter(l => l.qtd_cartoes === 0).map(l => l.id))}
              onExcluirUm={(id) => excluirLocais([id])}
              onMerge={mergeAmbiguo}
              onReload={carregarConferir}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

function ConferirManagerContent({ data, acao, onImportarTodos, onImportarUm, onMarcarClienteTodos, onMarcarClienteUm, onExcluirSemCartoes, onExcluirUm, onMerge, onReload }) {
  const [tab, setTab] = useState('so_manager');
  const tabs = [
    { id: 'so_manager',  label: 'Faltam importar', count: data.so_manager.length,  color: colors.warning },
    { id: 'so_local',    label: 'Lixo (não está no Manager)', count: data.so_local.length, color: colors.danger },
    { id: 'ambiguos',    label: 'Ambíguos (precisam merge)',  count: data.ambiguos.length, color: colors.info },
    { id: 'match_unico', label: 'OK (1:1)', count: data.match_unico.length, color: colors.success },
  ];
  const semCartoesCount = data.so_local.filter(l => l.qtd_cartoes === 0).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, fontSize: 13, color: colors.textMuted, flexWrap: 'wrap' }}>
        <span>Manager: <strong style={{ color: colors.text }}>{data.totais.manager}</strong></span>
        <span>Local: <strong style={{ color: colors.text }}>{data.totais.local}</strong></span>
        <Button size="sm" variant="outline" color={colors.textMuted} onClick={onReload}>↻ Atualizar</Button>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${colors.border}` }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
            color: tab === t.id ? t.color : colors.textMuted, fontWeight: 600, fontSize: 13,
            borderBottom: tab === t.id ? `2px solid ${t.color}` : '2px solid transparent',
          }}>
            {t.label} <span style={{ background: `${t.color}22`, color: t.color, padding: '1px 7px', borderRadius: 10, fontSize: 11, marginLeft: 4 }}>{t.count}</span>
          </button>
        ))}
      </div>

      {tab === 'so_manager' && (
        <div>
          {data.so_manager.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>Nada a importar — tudo que está no Manager já existe localmente.</div>
          ) : (
            <>
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: colors.textMuted }}>{data.so_manager.length} titulares no Manager sem correspondente local.</span>
                <Button color={colors.warning} disabled={acao === 'importar'} onClick={onImportarTodos}>
                  {acao === 'importar' ? 'Importando…' : `Importar todos (${data.so_manager.length})`}
                </Button>
              </div>
              <Table>
                <thead><tr><Th>XMiles ID</Th><Th>Nome</Th><Th>CPF</Th><Th>Status</Th><Th align="center">Ação</Th></tr></thead>
                <tbody>
                  {data.so_manager.map(m => (
                    <tr key={m.xmiles_id}>
                      <Td style={{ color: colors.textMuted, fontSize: 12 }}>{m.xmiles_id}</Td>
                      <Td style={{ fontWeight: 600 }}>{m.nome}</Td>
                      <Td style={{ color: colors.textMuted, fontSize: 12 }}>{m.cpf || '-'}</Td>
                      <Td><Badge text={m.status || '-'} color={colors.textDim} /></Td>
                      <Td style={{ textAlign: 'center' }}>
                        <Button size="sm" variant="outline" color={colors.warning} disabled={!!acao}
                          onClick={() => onImportarUm(m.xmiles_id)}>Importar</Button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </div>
      )}

      {tab === 'so_local' && (
        <div>
          {data.so_local.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>Nenhum titular local fora do Manager.</div>
          ) : (
            <>
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 13, color: colors.textMuted }}>
                  {data.so_local.length} titulares locais não existem no Manager. Geralmente: nomes de cartão importados como titular.
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="outline" color={colors.textMuted} disabled={!!acao} onClick={onMarcarClienteTodos}>
                    Marcar todos como CLIENTE
                  </Button>
                  {semCartoesCount > 0 && (
                    <Button color={colors.danger} disabled={!!acao} onClick={onExcluirSemCartoes}>
                      Excluir os {semCartoesCount} sem cartões
                    </Button>
                  )}
                </div>
              </div>
              <Table>
                <thead><tr><Th>ID</Th><Th>Nome</Th><Th>CPF</Th><Th align="center">Cartões</Th><Th>Fatura</Th><Th>Tipo</Th><Th align="center">Ações</Th></tr></thead>
                <tbody>
                  {data.so_local.map(l => (
                    <tr key={l.id}>
                      <Td style={{ color: colors.textMuted, fontSize: 12 }}>{l.id}</Td>
                      <Td style={{ fontWeight: 600 }}>{l.nome}</Td>
                      <Td style={{ color: colors.textMuted, fontSize: 12 }}>{l.cpf || '-'}</Td>
                      <Td style={{ textAlign: 'center', color: l.qtd_cartoes > 0 ? colors.text : colors.textDim }}>{l.qtd_cartoes}</Td>
                      <Td style={{ color: l.total_fatura > 0 ? colors.danger : colors.textDim }}>{fmt(l.total_fatura)}</Td>
                      <Td><Badge text={l.tipo === 'CLIENTE' ? 'Cliente' : 'Investidor'} color={l.tipo === 'CLIENTE' ? colors.textMuted : colors.primary} /></Td>
                      <Td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {l.tipo !== 'CLIENTE' && (
                            <Button size="sm" variant="outline" color={colors.textMuted} disabled={!!acao}
                              onClick={() => onMarcarClienteUm(l.id)}>→ Cliente</Button>
                          )}
                          {l.qtd_cartoes === 0 && (
                            <Button size="sm" variant="outline" color={colors.danger} disabled={!!acao}
                              onClick={() => onExcluirUm(l.id)}>Excluir</Button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </div>
      )}

      {tab === 'ambiguos' && (
        <div>
          {data.ambiguos.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>Nenhum titular ambíguo.</div>
          ) : (
            <>
              <div style={{ marginBottom: 12, fontSize: 13, color: colors.textMuted }}>
                {data.ambiguos.length} titular(es) do Manager batem com múltiplos locais (mesmo primeiro nome). Escolha quem mesclar em quem.
              </div>
              {data.ambiguos.map((g, i) => (
                <div key={i} style={{ background: colors.bg, borderRadius: 8, padding: 12, marginBottom: 10, border: `1px solid ${colors.border}` }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Manager: <span style={{ color: colors.info }}>{g.manager.nome}</span>
                    <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 8 }}>(xmiles_id: {g.manager.xmiles_id}, CPF: {g.manager.cpf || '-'})</span>
                  </div>
                  <Table>
                    <thead><tr><Th>ID local</Th><Th>Nome</Th><Th align="center">Cartões</Th><Th>Fatura</Th><Th align="center">Ação</Th></tr></thead>
                    <tbody>
                      {g.candidatos_locais.map(c => (
                        <tr key={c.id}>
                          <Td style={{ color: colors.textMuted, fontSize: 12 }}>{c.id}</Td>
                          <Td>{c.nome}</Td>
                          <Td style={{ textAlign: 'center' }}>{c.qtd_cartoes}</Td>
                          <Td>{fmt(Number(c.total_fatura) || 0)}</Td>
                          <Td style={{ textAlign: 'center' }}>
                            <MergeDropdown candidatos={g.candidatos_locais} sourceId={c.id} onMerge={onMerge} disabled={!!acao} />
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'match_unico' && (
        <div>
          {data.match_unico.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>Nenhum match 1:1.</div>
          ) : (
            <Table>
              <thead><tr><Th>Manager</Th><Th>Local</Th><Th>CPF</Th></tr></thead>
              <tbody>
                {data.match_unico.map((m, i) => (
                  <tr key={i}>
                    <Td>{m.manager.nome}</Td>
                    <Td>{m.local.nome}</Td>
                    <Td style={{ color: colors.textMuted, fontSize: 12 }}>{m.local.cpf || m.manager.cpf || '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}

function MergeDropdown({ candidatos, sourceId, onMerge, disabled }) {
  const [target, setTarget] = useState('');
  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
      <select value={target} onChange={e => setTarget(e.target.value)}
        style={{ background: colors.input, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
        <option value="">→ mesclar em…</option>
        {candidatos.filter(c => c.id !== sourceId).map(c => (
          <option key={c.id} value={c.id}>#{c.id} {c.nome} ({c.qtd_cartoes} cart.)</option>
        ))}
      </select>
      <Button size="sm" variant="outline" color={colors.info} disabled={!target || disabled}
        onClick={() => onMerge(sourceId, parseInt(target))}>Merge</Button>
    </div>
  );
}
